import React, { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import DictionaryApiService from "../../system/integrate/DictionaryApiService";
import BannedService from "./BannedService";
import Logger from "../../../services/Logger";

const BANNED_CATEGORY_REASONS = {
    profanity: "Profanity",
    violence: "Violence",
    drugs: "Drugs",
    offensive: "Offensive",
    inappropriate: "Inappropriate",
    high_frequency: "High-frequency word (too common)",
    other: "Other",
};

function formatBannedReason(banned) {
    const category = String(banned?.category || "").trim().toLowerCase();
    const reason = BANNED_CATEGORY_REASONS[category]
        || category.split("_").filter(Boolean).map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(" ")
        || "Banned";
    if (banned?.isKids) {
        return `${reason} (kids)`;
    }
    return reason;
}

function matchesBannedEntry(banned, word) {
    if (!banned || !word) return false;
    const target = String(word).trim().toLowerCase();
    if (!target) return false;
    const w = String(banned.word || "").trim().toLowerCase();
    const n = String(banned.wordNorm || "").trim().toLowerCase();
    return target === w || (n && target === n);
}

/** Prefer canonical `word` (e.g. "mi-temps") over grid `text` (e.g. "mitemps") for dictionary and banned-word checks. */
function validationWordFromCoord(coord) {
    if (!coord) return null;
    if (typeof coord.word === "string" && coord.word.trim() !== "") {
        return coord.word.trim();
    }
    if (typeof coord.text === "string" && coord.text.trim() !== "") {
        return coord.text.trim();
    }
    return null;
}

export default function WordValidation({
    filledCoordinates,
    language = "english",
    isKids = false,
    difficultyLevel = null,
    /** When true (Word Wheel), high_frequency banned words are always allowed. */
    allowHighFrequency = false,
}) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [bannedWords, setBannedWords] = useState([]);
    const [wordsWithoutMeaning, setWordsWithoutMeaning] = useState([]);

    useEffect(() => {
        const fetchWordValidation = async () => {
            if (!filledCoordinates || filledCoordinates.length === 0) {
                setBannedWords([]);
                setWordsWithoutMeaning([]);
                return;
            }

            setLoading(true);
            setError(null);

            try {
                const wordTexts = filledCoordinates
                    .map(validationWordFromCoord)
                    .filter((w) => w != null);

                if (wordTexts.length === 0) {
                    setBannedWords([]);
                    setWordsWithoutMeaning([]);
                    return;
                }

                // Fetch banned words and check for inappropriate words
                const bannedWordsData = await BannedService.requestList(language);

                // Filter banned words based on isKids flag and difficultyLevel
                // If puzzle is not for kids, exclude words that are only banned for kids (isKids = true)
                // When puzzle is EASY (or Word Wheel), exclude high_frequency banned words
                const puzzleIsKids = Boolean(isKids);
                const excludeHighFrequency = allowHighFrequency || difficultyLevel === "EASY";
                const relevantBannedWords = bannedWordsData.filter((banned) => {
                    if (!banned) return false;
                    // When EASY / Word Wheel, skip high_frequency category
                    if (excludeHighFrequency && banned.category === "high_frequency") return false;
                    // If puzzle is for kids, include all other banned words
                    if (puzzleIsKids) return true;
                    // If puzzle is not for kids, exclude words that are only banned for kids
                    return !banned.isKids;
                });

                const foundBannedWords = [];
                const seenBannedKeys = new Set();
                for (const word of wordTexts) {
                    const hit = relevantBannedWords.find((banned) => matchesBannedEntry(banned, word));
                    if (!hit) continue;
                    const key = String(hit.id || hit.wordNorm || hit.word || word).trim().toLowerCase();
                    if (seenBannedKeys.has(key)) continue;
                    seenBannedKeys.add(key);
                    foundBannedWords.push({
                        word: hit.word || word,
                        category: hit.category,
                        isKids: Boolean(hit.isKids),
                    });
                }
                setBannedWords(foundBannedWords);

                // Fetch word meanings and check for words without meanings
                const wordsInfo = await DictionaryApiService.getMultipleWordsInfo(wordTexts, language);
                
                // Create a map of words that have meanings, using wordNorm if available, otherwise word
                const wordsWithMeanings = new Set();
                wordsInfo.forEach(wordInfo => {
                    // Add the word itself
                    if (wordInfo.word) {
                        wordsWithMeanings.add(wordInfo.word.toLowerCase());
                    }
                    // Add wordNorm if available (e.g., "re-solve" has wordNorm "resolve")
                    if (wordInfo.wordNorm) {
                        wordsWithMeanings.add(wordInfo.wordNorm.toLowerCase());
                    }
                });

                // Find words that don't have meanings (not in the wordsInfo response)
                // Compare using the word itself, and also check if it matches any wordNorm
                const wordsWithoutMeanings = wordTexts.filter(word => {
                    const wordLower = word.toLowerCase();
                    // Check if the word matches any wordInfo.word or wordInfo.wordNorm
                    return !wordsInfo.some(wordInfo => {
                        const infoWordLower = wordInfo.word?.toLowerCase();
                        const infoWordNormLower = wordInfo.wordNorm?.toLowerCase();
                        return wordLower === infoWordLower || wordLower === infoWordNormLower;
                    });
                });

                // Also check words that have empty meanings
                const wordsWithEmptyMeanings = wordsInfo.filter(wordInfo => {
                    // Check if the word has no meaning or empty meaning
                    return !wordInfo.meaning ||
                        Object.keys(wordInfo.meaning).length === 0 ||
                        (Object.keys(wordInfo.meaning).length === 1 && wordInfo.meaning.title);
                });

                // Combine both types of words without meanings
                const allWordsWithoutMeanings = [
                    ...wordsWithoutMeanings.map(word => ({ word, meaning: null })),
                    ...wordsWithEmptyMeanings
                ];

                setWordsWithoutMeaning(allWordsWithoutMeanings);

            } catch (err) {
                Logger.error('Error fetching word validation data:', err);
                setError('Failed to validate words');
            } finally {
                setLoading(false);
            }
        };

        fetchWordValidation();
    }, [filledCoordinates, language, isKids, difficultyLevel, allowHighFrequency]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">Validating words...</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ mb: 0 }}>
                {error}
            </Alert>
        );
    }

    // If no issues found
    if (bannedWords.length === 0 && wordsWithoutMeaning.length === 0) {
        return (
            <Alert severity="success" sx={{ mb: 0 }}>
                All words are valid and have meanings available
            </Alert>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Banned words section */}
            {bannedWords.length > 0 && (
                <Alert severity="warning" sx={{ mb: 0 }}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Banned Words Found ({bannedWords.length})
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                        {bannedWords.map((banned, index) => (
                            <Typography key={`${banned.word}-${banned.category}-${index}`} variant="body2">
                                <Box component="span" sx={{ fontWeight: 700 }}>{banned.word}</Box>
                                {" — "}
                                {formatBannedReason(banned)}
                            </Typography>
                        ))}
                    </Box>
                </Alert>
            )}

            {/* Words Without Meaning Section */}
            {wordsWithoutMeaning.length > 0 && (
                <Alert severity="warning" sx={{ mb: 0 }}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Words Without Meanings ({wordsWithoutMeaning.length})
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        The following words don't have definitions available in the dictionary:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                        {wordsWithoutMeaning.map((wordInfo, index) => (
                            <Box
                                key={wordInfo.id || index}
                                sx={{
                                    padding: '4px 8px',
                                    borderRadius: 1,
                                    backgroundColor: 'warning.light',
                                    color: 'warning.contrastText',
                                    border: '1px solid',
                                    borderColor: 'warning.main'
                                }}
                            >
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {wordInfo.word}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                        These words might be proper nouns, abbreviations, or very rare terms not in the dictionary database.
                    </Typography>
                </Alert>
            )}
        </Box>
    );
}
